/**
 * Render service — canvas rendering pipeline.
 * Replaces window.RenderService.
 */
import { loadImage, getInitialScale } from '../utils';
import type { Transform } from '../stores/types';
import type { Product } from '../stores/appStore';

/** Normalize transform values (guard against NaN/undefined) */
export function normalizeTransform(transform?: Partial<Transform> | null): Transform {
  return {
    x: Number.isFinite(transform?.x) ? transform!.x! : 0,
    y: Number.isFinite(transform?.y) ? transform!.y! : 0,
    scale: Math.min(10, Math.max(0.05, Number.isFinite(transform?.scale) ? transform!.scale! : 1)),
    rotation: Number.isFinite(transform?.rotation) ? transform!.rotation! : 0,
  };
}

/** Apply print transform on a canvas context */
export function applyPrintTransform(
  ctx: CanvasRenderingContext2D,
  printImg: HTMLImageElement,
  transform: Transform,
  canvasWidth: number,
  canvasHeight: number,
): void {
  const t = normalizeTransform(transform);
  ctx.save();
  ctx.translate(canvasWidth / 2 + t.x, canvasHeight / 2 + t.y);
  ctx.rotate((t.rotation * Math.PI) / 180);
  ctx.scale(t.scale, t.scale);
  ctx.drawImage(printImg, -printImg.width / 2, -printImg.height / 2);
  ctx.restore();
}

/** Apply mask using destination-in compositing */
export function applyMask(
  ctx: CanvasRenderingContext2D,
  maskImg: HTMLImageElement | null,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (!maskImg) return;
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(maskImg, 0, 0, canvasWidth, canvasHeight);
  ctx.globalCompositeOperation = 'source-over';
}

/** Apply overlay on top */
export function applyOverlay(
  ctx: CanvasRenderingContext2D,
  overlayImg: HTMLImageElement | null,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (!overlayImg) return;
  ctx.globalCompositeOperation = 'source-over';
  if (overlayImg.width > 0 && overlayImg.height > 0) {
    const scale = Math.max(canvasWidth / overlayImg.width, canvasHeight / overlayImg.height);
    const sw = overlayImg.width * scale;
    const sh = overlayImg.height * scale;
    ctx.drawImage(overlayImg, (canvasWidth - sw) / 2, (canvasHeight - sh) / 2, sw, sh);
  }
}

/** Build default transforms for all enabled products */
export function buildDefaultTransforms(products: Product[]): Record<string, Transform> {
  const map: Record<string, Transform> = {};
  products.forEach((p) => {
    if (!p.enabled) return;
    map[p.id] = { x: 0, y: 0, scale: 0.6, rotation: 0 };
  });
  return map;
}

/** Initialize transforms based on image/product fit */
export async function initializeTransforms(
  file: { url: string },
  products: Product[],
): Promise<Record<string, Transform>> {
  const fallback = () => buildDefaultTransforms(products);
  try {
    const img = await loadImage(file.url);
    if (!img) return fallback();

    const map: Record<string, Transform> = {};
    products.forEach((p) => {
      if (!p.enabled) return;
      const w = p.width || 1000;
      const h = p.height || 1000;
      const scale = getInitialScale(w, h, img.width, img.height);
      const safeScale = Number.isFinite(scale) ? scale * 0.9 : 0.6;
      map[p.id] = { x: 0, y: 0, scale: safeScale, rotation: 0 };
    });
    return map;
  } catch {
    return fallback();
  }
}

/** Get transform for a specific product based on mode */
export function getTransformByMode(
  transforms: Record<string, Transform>,
  productTransforms: Record<string, Transform>,
  mode: string,
  productId: string,
  fallbackScale = 0.5,
): Transform {
  const map = mode === 'products' ? productTransforms : transforms;
  return map[productId] || { x: 0, y: 0, scale: fallbackScale, rotation: 0 };
}

/** Render mockup to Blob */
export async function renderMockupBlob(
  product: Product,
  printImg: HTMLImageElement,
  transform: Transform,
  mockupDPI: number,
  mockupWidth: number | undefined,
  mockupHeight: number | undefined,
  options: {
    maskUrl?: string;
    overlayUrl?: string;
    mimeType?: string;
    backgroundColor?: string | null;
    renderRotation?: number;
    outputWidth?: number;
    outputHeight?: number;
  } = {},
): Promise<Blob | null> {
  const maskUrl = options.maskUrl !== undefined ? options.maskUrl : product.mask;
  const overlayUrl = options.overlayUrl !== undefined ? options.overlayUrl : product.overlay;

  const [base, mask, overlay] = await Promise.all([
    loadImage(product.image || null),
    loadImage(maskUrl || null),
    loadImage(overlayUrl || null),
  ]);

  let width = mockupWidth || (base ? base.width : options.outputWidth || product.width || 1000);
  let height =
    mockupHeight || (base ? base.height : options.outputHeight || product.height || 1000);

  if (mockupWidth && !mockupHeight && base) {
    height = Math.round((mockupWidth / base.width) * base.height);
  } else if (mockupHeight && !mockupWidth && base) {
    width = Math.round((mockupHeight / base.height) * base.width);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, width, height);

  // White background
  if (options.backgroundColor !== null) {
    ctx.fillStyle = options.backgroundColor || '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }

  // Layer 1: Base product image
  if (base) ctx.drawImage(base, 0, 0, width, height);

  // Layer 2: Print with transform + mask
  if (printImg) {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.clearRect(0, 0, width, height);

    applyPrintTransform(tempCtx, printImg, transform, width, height);
    applyMask(tempCtx, mask, width, height);

    ctx.globalCompositeOperation = (product.blendMode as GlobalCompositeOperation) || 'source-over';
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
  }

  // Layer 3: Overlay
  applyOverlay(ctx, overlay, width, height);

  const mimeType = options.mimeType || 'image/png';
  let blob: Blob | null;

  if (options.renderRotation && Math.abs(options.renderRotation) === 90) {
    const rotCanvas = document.createElement('canvas');
    rotCanvas.width = height;
    rotCanvas.height = width;
    const rotCtx = rotCanvas.getContext('2d')!;
    rotCtx.translate(rotCanvas.width / 2, rotCanvas.height / 2);
    rotCtx.rotate((options.renderRotation * Math.PI) / 180);
    rotCtx.drawImage(canvas, -width / 2, -height / 2);
    blob = await new Promise<Blob | null>((resolve) => {
      rotCanvas.toBlob(resolve, mimeType, mimeType === 'image/png' ? undefined : 0.9);
    });
  } else {
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mimeType, mimeType === 'image/png' ? undefined : 0.9);
    });
  }

  // DPI metadata for PNG
  if (blob && mimeType === 'image/png' && mockupDPI) {
    const { setPNGDPI } = await import('./pngService');
    blob = await setPNGDPI(blob, mockupDPI);
  }

  return blob;
}
